import { useMediaQuery } from "./useMediaQuery";

/**
 * tailwind's default breakpoints
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Returns true when the viewport is at least as wide as the named breakpoint
 * (i.e. a min-width match, same semantics as Tailwind's responsive prefixes).
 *
 */
export function useBreakpoint(breakpoint: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]}px)`);
}

/**
 * Returns the full match-state for every breakpoint at once.
 * Useful when you need to branch on several sizes in the same component
 * without calling useBreakpoint multiple times.
 *
 *  @example
 * const current = useBreakpoints(); // { sm: true, md: false, ...}
 */
export function useBreakpoints(): Record<Breakpoint, boolean> {
  const xs = useBreakpoint("xs");
  const sm = useBreakpoint("sm");
  const md = useBreakpoint("md");
  const lg = useBreakpoint("lg");
  const xl = useBreakpoint("xl");
  const xxl = useBreakpoint("2xl");

  return { xs, sm, md, lg, xl, "2xl": xxl };
}

/**
 * Returns the *largest* breakpoint that currently matches
 * Returns undefined when the viewport is narrower than the smallest breakpoint.
 *
 * @example
 * const current = useCurrentBreakpoint(); // "lg"
 */
export function useCurrentBreakpoint(): Breakpoint {
  const bp = useBreakpoints();
  const ordered: Breakpoint[] = ["2xl", "xl", "lg", "md", "sm", "xs"];
  return ordered.find((key) => bp[key]) ?? "xs";
}
